import { forwardRef } from "react";
import { Mountain } from "lucide-react";
import { fmtValue } from "./reviewStats";
import { Stat, tone } from "./YearReviewCard";

function MonthCalendar({ dayMap, ym, lang }) {
  const [y, m] = ym.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const lead = (first.getDay() + 6) % 7;
  const count = new Date(y, m, 0).getDate();
  const max = Math.max(0, ...Object.values(dayMap).map(Math.abs));
  const dayFmt = new Intl.DateTimeFormat(lang, { weekday: "narrow" });
  const cells = [...Array(lead).fill(null), ...Array.from({ length: count }, (_, i) => i + 1)];
  const bg = (v) => {
    if (v == null) return "rgba(255,255,255,0.05)";
    const a = 0.35 + (max ? Math.abs(v) / max : 0) * 0.65;
    return v > 0 ? `rgba(16,185,129,${a})` : v < 0 ? `rgba(244,63,94,${a})` : "rgba(148,163,184,0.5)";
  };
  return (
    <div className="grid grid-cols-7 gap-1.5">
      {[1, 2, 3, 4, 5, 6, 0].map((wd) => <div key={wd} className="text-center text-[9px] uppercase text-slate-500 font-semibold">{dayFmt.format(new Date(2024, 0, wd === 0 ? 7 : wd))}</div>)}
      {cells.map((d, i) => {
        const key = d && `${ym}-${String(d).padStart(2, "0")}`;
        return d == null ? <div key={`x${i}`} /> : (
          <div key={key} style={{ background: bg(dayMap[key]) }} className="h-9 rounded-md flex items-start justify-end p-1 text-[9px] font-semibold text-white/80">{d}</div>
        );
      })}
    </div>
  );
}

export const MonthReviewCard = forwardRef(function MonthReviewCard({ r, ym, mode, name, change, t, lang }, ref) {
  const [y, m] = ym.split("-").map(Number);
  const label = new Intl.DateTimeFormat(lang, { month: "long", year: "numeric" }).format(new Date(y, m - 1, 1));
  const prevLabel = new Intl.DateTimeFormat(lang, { month: "short" }).format(new Date(y, m - 2, 1));
  const v = (n) => fmtValue(n, mode);
  const dayLabel = r.bestDay && new Date(`${r.bestDay.k}T12:00:00`).toLocaleDateString(lang, { day: "numeric", month: "short" });
  return (
    <div ref={ref} data-testid="month-review-card" className="relative w-[540px] overflow-hidden rounded-3xl bg-slate-950 p-7 text-white">
      <div className="absolute -top-24 -right-20 h-72 w-72 rounded-full bg-amber-500/20 blur-3xl" />
      <div className="absolute -bottom-24 -left-20 h-72 w-72 rounded-full bg-blue-600/25 blur-3xl" />
      <div className="relative">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-amber-400 font-semibold"><Mountain size={14} /> Peak Market Society</div>
        <div className="mt-3 font-display font-black text-4xl tracking-tight leading-none first-letter:uppercase">{t("insights.review.monthCardTitle", { month: label })}</div>
        {name && <div className="mt-1.5 text-sm text-slate-300">{name}</div>}
        <div className="mt-5 flex items-end gap-4">
          {mode !== "hide" && <div data-testid="month-review-total" className={`font-display font-black text-5xl tracking-tight leading-none ${tone(r.total, mode)}`}>{v(r.total)}</div>}
          <div className="pb-1 text-xs text-slate-400 leading-snug">
            {t("insights.review.tradesLine", { count: r.trades })}<br />{t("insights.review.winRateLine", { wr: r.winRate })}
          </div>
          {change != null && mode !== "hide" && (
            <div data-testid="month-review-change" className={`ml-auto mb-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${change >= 0 ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300"}`}>
              {t("insights.review.vsPrev", { pct: `${change >= 0 ? "+" : "−"}${Math.abs(change)}%`, month: prevLabel })}
            </div>
          )}
        </div>
        <div className="mt-5 rounded-2xl bg-white/[0.03] border border-white/10 p-3"><MonthCalendar dayMap={r.dayMap} ym={ym} lang={lang} /></div>
        <div className="mt-4 grid grid-cols-3 gap-2">
          <Stat label={t("insights.review.greenDays")} value={`${r.greenDays}/${r.tradingDays}`} />
          <Stat label={t("insights.review.bestSingleDay")} value={dayLabel} sub={r.bestDay && v(r.bestDay.v)} cls="text-emerald-400" />
          <Stat label={t("insights.review.streak")} value={r.streak} sub={t("insights.review.streakSub")} />
          <Stat label={t("insights.review.topInstrument")} value={r.topInstrument?.k} sub={r.topInstrument && t("insights.review.tradesLine", { count: r.topInstrument.n })} />
          <Stat label={t("insights.review.bestSession")} value={r.bestSession ? t(`sessions.${r.bestSession.k}`) : null} sub={r.bestSession && v(r.bestSession.v)} />
          <Stat label={t("insights.review.avgGrade")} value={r.topGrade} sub={r.graded ? t("insights.review.gradedSub", { count: r.graded, score: r.avgScore }) : null} cls="text-amber-400" />
        </div>
        {r.badges.length > 0 && <div className="mt-3 text-[11px] text-amber-300">{t("insights.review.badgesMonth", { count: r.badges.length })}</div>}
        <div className="mt-5 text-[10px] text-slate-500">{t("insights.review.footer")}</div>
      </div>
    </div>
  );
});
